import { Link, useNavigate } from 'react-router-dom';
import React, { CSSProperties, useEffect, useState } from 'react';

import AlertDialog from '../../shared/components/AlertDialog';
import AssetService from '../../shared/services/AssetService';
import AuthService from '../../shared/services/AuthService';
import Avatar from '@mui/material/Avatar';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CertIcon from './CertIcon';
import CertService from '../../report/services/CertService';
import CourseProgressBarContainer from './CourseProgressBarContainer';
import CourseStatus from '../models/CourseStatus';
import FileService from '../../shared/services/FileService';
import Grid from '@mui/material/Unstable_Grid2';
import Overlay from '../../shared/components/Overlay';
import Pathway from '../models/Pathway';
import Typography from '@mui/material/Typography';
import styles from '../styles/CourseCard.module.css';
import { useTheme } from '@mui/material/styles';

interface CourseCardProps {
  pathway: Pathway;
  thumbnail?: string;
  isVisible: boolean;
  onStatusLoad: (pathkey: string, status: CourseStatus) => void;
}

const CourseCard = (props: CourseCardProps) => {
  const [avatarStyle, setAvatarStyle] = useState<CSSProperties>();
  const [avatarImage, setAvatarImage] = useState('');
  const muiTheme = useTheme();
  const primaryColor = muiTheme.palette.primary.main;
  const userkey = AuthService.Userkey;
  const [fetchingCert, setFetchingCert] = useState(false);
  const [fetchCertError, setFetchCertError] = useState(false);

  const certificateStatus = props.pathway.certificateStatus;

  useEffect(() => {
    setAvatarStyle({
      backgroundColor: primaryColor
    });
  }, []);

  useEffect(() => {
    if (props.pathway) {
      const displaySettings = props.pathway.displaySettings;
      if (!displaySettings) {
        return;
      }

      if (displaySettings.icon?.type === 'color') {
        setAvatarStyle({
          backgroundColor: displaySettings.icon.value || primaryColor
        });
      }

      if (displaySettings.icon?.type === 'image') {
        setAvatarImage(AssetService.getAssetUrl(displaySettings.icon.value));
      }
    }
  }, [props.pathway]);

  /** Download a certificate in response to clicking its icon. */
  const handleCertificateClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    setFetchingCert(true);
    try {
      const cert = await CertService.getCourseCertificate(
        userkey,
        +props.pathway.pathkey
      );
      setFetchingCert(false);
      FileService.open(cert);
    } catch (error) {
      setFetchingCert(false);
      setFetchCertError(true);
    }
  };

  const handleCertErrorClose = () => {
    setFetchCertError(false);
  };

  const avatarText = props.pathway.title.toUpperCase()[0];

  return (
    <>
      <Card className={props.thumbnail ? styles.card : styles.cardNoThumbnail}>
        <CardActionArea>
          <Link
            to={`/dashboard/course/${props.pathway.pathkey}`}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <CardContent className={styles.cardContent}>
              <Grid container direction="column" spacing={1}>
                <Grid style={{ position: 'relative' }}>
                  <Grid container spacing={1} wrap="nowrap" alignItems="center">
                    <Grid>
                      <Avatar
                        color="primary"
                        style={avatarStyle}
                        src={avatarImage}
                      >
                        {avatarText}
                      </Avatar>
                    </Grid>
                    <Grid>
                      <Grid container wrap="nowrap" direction="column">
                        <Grid>
                          <Typography
                            variant="button"
                            style={{ fontSize: '0.8em' }}
                          >
                            COURSE
                          </Typography>
                        </Grid>
                        <Grid xs>
                          <Typography
                            variant="subtitle1"
                            noWrap
                            style={{ maxWidth: '220px' }}
                          >
                            {props.pathway.title}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid className={styles.certIcon}>
                      {certificateStatus !== 'none' && (
                        <CertIcon
                          status={certificateStatus}
                          onClick={handleCertificateClick}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Grid>

                {!props.pathway.wizardpath && (
                  <Grid>
                    <CourseProgressBarContainer
                      pathway={props.pathway}
                      visible={props.isVisible}
                      onStatusLoad={props.onStatusLoad}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Link>
        </CardActionArea>
      </Card>

      {fetchingCert && <Overlay spinner message="Downloading certificate..." />}
      {fetchCertError && (
        <AlertDialog
          title="View Certificate"
          message="Failed to download the certificate."
          onClose={handleCertErrorClose}
        />
      )}
    </>
  );
};

export default CourseCard;
